import React, { useState, useEffect } from 'react';
import { FiChevronDown, FiChevronUp, FiX, FiCheck, FiAlertCircle, FiUpload } from 'react-icons/fi';
import { FaCircleCheck, FaCircleExclamation } from 'react-icons/fa6';
import { ClipLoader } from 'react-spinners';

export type UploadStage = 'uploading' | 'processing' | 'completed' | 'error';

export interface TrackedUpload {
  id: string;
  fileName: string;
  fileSize: number;
  stage: UploadStage;
  progress: number; // 0-100 for uploading stage
  error?: string;
  retryCount?: number;
  maxRetries?: number;
  startTime: Date;
  endTime?: Date;
}

interface UploadProgressTrackerProps {
  uploads: TrackedUpload[];
  onClose?: () => void;
  onRemoveUpload?: (id: string) => void;
}

const UploadProgressTracker: React.FC<UploadProgressTrackerProps> = ({
  uploads,
  onClose,
  onRemoveUpload,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  // Show the tracker when there are uploads
  useEffect(() => {
    if (uploads.length > 0) {
      setIsVisible(true);
    }
  }, [uploads.length]);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStageIcon = (stage: UploadStage) => {
    switch (stage) {
      case 'uploading':
        return <FiUpload className="w-4 h-4 text-blue-500" />;
      case 'processing':
        return <ClipLoader size={16} color="#3B82F6" />;
      case 'completed':
        return <FaCircleCheck className="w-4 h-4 text-green-500" />;
    }
  };

  const getStageText = (stage: UploadStage) => {
    switch (stage) {
      case 'uploading':
        return 'Uploading...';
      case 'processing':
        return 'Processing...';
      case 'completed':
        return 'Completed';
      case 'error':
        return 'Failed';
    }
  };

  const getTimeElapsed = (startTime: Date, endTime?: Date) => {
    const end = endTime || new Date();
    const diff = Math.floor((end.getTime() - startTime.getTime()) / 1000);

    if (diff < 60) return `${diff}s`;
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;
    return `${minutes}m ${seconds}s`;
  };

  const activeUploads = uploads.filter(u => u.stage === 'uploading' || u.stage === 'processing');
  const completedUploads = uploads.filter(u => u.stage === 'completed');
  const errorUploads = uploads.filter(u => u.stage === 'error');

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 right-4 z-40 w-80 max-w-full">
      <div className="bg-white rounded-t-lg shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div
          className="bg-blue-50 text-gray-800 px-4 py-3 cursor-pointer select-none border-b border-gray-300"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FiUpload className="w-5 h-5 text-gray-700" />
              <span className="font-medium text-gray-900">
                {activeUploads.length > 0
                  ? 'Processing Uploads'
                  : completedUploads.length > 0
                    ? `${completedUploads.length} Upload${completedUploads.length === 1 ? '' : 's'} Processed`
                    : 'Upload Progress'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(!isExpanded);
                }}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                {isExpanded ? <FiChevronDown className="w-4 h-4 text-gray-600" /> : <FiChevronUp className="w-4 h-4 text-gray-600" />}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsVisible(false);
                  // Clear ALL uploads when closing
                  if (onRemoveUpload) {
                    uploads.forEach(u => onRemoveUpload(u.id));
                  }
                  if (onClose) onClose();
                }}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                <FiX className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {isExpanded && (
          <>
            <div className="max-h-96 overflow-y-auto">
              {uploads.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <FiUpload className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">No active uploads</p>
                </div>
              ) : (
                <div className="p-2">
                  {/* Active uploads */}
                  {activeUploads.map(upload => (
                  <div key={upload.id} className="p-3 bg-gray-50 rounded-lg mb-2 overflow-hidden">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        {getStageIcon(upload.stage)}
                        <div className="flex-1 min-w-0 pr-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {upload.fileName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(upload.fileSize)} • {getStageText(upload.stage)}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">
                        {getTimeElapsed(upload.startTime)}
                      </span>
                    </div>

                    {upload.stage === 'uploading' && (
                      <div>
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Uploading to server...</span>
                          <span>{upload.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${upload.progress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {upload.stage === 'processing' && (
                      <div className="text-xs text-gray-600 mt-2">
                        Processing file for platform integration...
                      </div>
                    )}
                  </div>
                ))}

                {/* Error uploads */}
                {errorUploads.map(upload => (
                  <div key={upload.id} className="p-3 bg-gray-50 rounded-lg mb-2 overflow-hidden">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <div className="flex-1 min-w-0 pr-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {upload.fileName}
                          </p>
                          <p className="text-xs text-red-600">
                            This file could not be processed. {upload.retryCount}/{upload.maxRetries} retries failed.
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            The team has been alerted
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <FaCircleExclamation className="w-5 h-5 text-red-500" />
                        {onRemoveUpload && (
                          <button
                            onClick={() => onRemoveUpload(upload.id)}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-100 rounded transition-colors"
                            title="Remove"
                          >
                            <FiX className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Completed uploads */}
                {completedUploads.map(upload => (
                  <div key={upload.id} className="p-3 bg-gray-50 rounded-lg mb-2 overflow-hidden">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <div className="flex-1 min-w-0 pr-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {upload.fileName}
                          </p>
                          <p className="text-xs text-green-600">
                            Successfully processed
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <FaCircleCheck className="w-5 h-5 text-green-500" />
                        {onRemoveUpload && (
                          <button
                            onClick={() => onRemoveUpload(upload.id)}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          >
                            <FiX className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Completed in {getTimeElapsed(upload.startTime, upload.endTime)}
                    </div>
                  </div>
                  ))}
                </div>
              )}
            </div>

            {/* Summary footer - Sticky at bottom */}
            {uploads.length > 0 && (
              <div className="border-t border-gray-200 px-4 py-2 bg-gray-50">
                <div className="flex items-center justify-between text-xs text-gray-600">
                  <div className="flex items-center gap-3">
                    {activeUploads.length > 0 && (
                      <span className="flex items-center gap-1">
                        <ClipLoader size={10} color="#3B82F6" />
                        {activeUploads.length} processing
                      </span>
                    )}
                    {completedUploads.length > 0 && (
                      <span className="flex items-center gap-1">
                        <FiCheck className="w-3 h-3 text-green-500" />
                        {completedUploads.length} completed
                      </span>
                    )}
                    {errorUploads.length > 0 && (
                      <span className="flex items-center gap-1">
                        <FiAlertCircle className="w-3 h-3 text-red-500" />
                        {errorUploads.length} failed
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (onRemoveUpload) {
                        completedUploads.forEach(u => onRemoveUpload(u.id));
                        errorUploads.forEach(u => onRemoveUpload(u.id));
                      }
                    }}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Clear finished
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UploadProgressTracker;